<script lang="ts">
    import { LoginService } from "$/services/LoginService";
    import { UserService } from "$/services/UserService";
    import { TokenStore } from "$/stores/TokenStore";
    import { getBrowser, checkIfExtensionInstalled } from "$/utils/Utils";
    import { clickAction } from "$/utils/clickAction";
    import { portal } from "$/utils/portal";
    import { onMount } from "svelte";
    import logo from "../../assets/images/logo.svg";
    import SvgIcon from "../SVGIcon.svelte";
    import { redirect } from "@roxi/routify";
    import { IntroModalStore } from "./IntroModal/IntroModalStore";

    enum STEPS {
        STEP1 = 1,
        STEP2 = 2,
        STEP3 = 3,
    }

    // TODO: Think of a better solution than using @html
    const roleOptions = [
        {
            label: "Marketer",
            description:
                "Streamline research and collaborate with team & clients",
            valueProposition: "Save up to <b>2 hours</b> daily",
        },
        {
            label: "Founder",
            description:
                "Do market research, share knowledge & collaborate on documents",
            valueProposition: "Make insights <b>actionable</b>",
        },
        {
            label: "Consultant",
            description: "Never do the same research twice",
            valueProposition: "<b>Keep track</b> of your sources",
        },
        {
            label: "Investor",
            description: "Do comparative research and fly over documents",
            valueProposition: "Save up to <b>2 days</b> on due diligence",
        },
        {
            label: "Lawyer",
            description:
                "Cross-source research, PDF collaboration, & client communication",
            valueProposition: "<b>Bring structure</b> to your know-hows",
        },
        {
            label: "Researcher",
            description: "Dive deep into sources and co-research topics",
            valueProposition: "Make research <b>organized</b>",
        },
        {
            label: "Student",
            description: "Save, organize & collaborate on research insights",
            valueProposition: "<b>One tool</b> all your notes",
        },
    ];

    const teamSizeOptions = ["1-9", "10-19", "20-49", "50+"];

    let questionaireVisible = false;
    let activeStep = STEPS.STEP1;

    function updateStep(step: STEPS) {
        activeStep = step;

        const url = new URL(window.location.href);
        url.search = `?step=${step}`;
        history.pushState({ step }, "", url.href);
    }

    function toggleRole(roleLabel: string) {
        if (selectedRoles.includes(roleLabel))
            selectedRoles = selectedRoles.filter((role) => role !== roleLabel);
        else selectedRoles = [...selectedRoles, roleLabel];
    }

    let selectedRoles = [];
    let otherRoleActive = false;
    let otherRoleValue = "";
    let selectedStatuses = [];
    let selectedTeamSize = null;

    let isExtensionInstalled = false;

    $: {
        selectedStatuses = roleOptions
            .filter((role) => selectedRoles.includes(role.label))
            .map((role) => role.label);
    }

    function toggleOtherRoleActive() {
        otherRoleActive = !otherRoleActive;
    }

    function stepOneAction() {
        updateStep(STEPS.STEP2);
    }

    function stepTwoAction() {
        submitForm();

        $redirect("/intro");
    }

    function updateTeamSize(teamSize) {
        selectedTeamSize = teamSize;
    }

    async function submitForm() {
        try {
            let userRole = `${selectedRoles.toString()}${otherRoleValue && "," + otherRoleValue}`;

            // TODO: Add Intercom to window object
            // @ts-ignore
            window.Intercom("update", {
                user_role: userRole,
                team_size: selectedTeamSize,
            });

            let userRoleList = selectedRoles;
            if (otherRoleValue) {
                userRoleList = [...selectedRoles, otherRoleValue];
            }

            const metadata = {
                userRole: userRoleList,
                teamSize: selectedTeamSize,
            };

            await UserService.saveUserMetadata(metadata);
            await UserService.questionnaireDone($TokenStore.user.id);
            await LoginService.refreshTokenLogin();

            $TokenStore.user.questionnaireDone = true;
            $IntroModalStore.userRoles = selectedRoles;

            // Clean up the history
            const url = new URL(window.location.href);
            url.search = "";
            history.replaceState({}, "", url.href);
        } catch (error) {
            console.error("An error occurred in submitForm:", error);
        }
    }

    const isChromeBrowser = getBrowser() === "chrome";
    const CHROME_STORE_EXTENSION_URL =
        "https://chrome.google.com/webstore/detail/collabwriting-shareable-n/jjefmmjicedljonbpdoeceklnhobmolo";
    const EDGE_STORE_EXTENSION_URL =
        "https://microsoftedge.microsoft.com/addons/detail/collabwriting-shareable/oaaidbemenheminahjaojcafppklekjd";

    onMount(async () => {
        if (!!!$TokenStore.user.questionnaireDone) {
            questionaireVisible = true;
        }

        isExtensionInstalled = await checkIfExtensionInstalled();

        // Add a history entry for step 1
        const url = new URL(window.location.href);
        url.search = `?step=${STEPS.STEP1}`;
        history.replaceState({ step: STEPS.STEP1 }, "", url.href);

        // handle history back/forward navigation
        function handlePopState(event) {
            if (event.state && event.state.step) {
                activeStep = event.state.step;
            }
        }

        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    });
</script>

{#if questionaireVisible}
    <div class="questionnaire-wrapper" use:portal>
        <div class="questionnaire-content">
            <img class="logo" src={logo} alt="logo" />
            <div class="step-indicator-wrapper">
                <span class="active-step-number"
                    >Step {activeStep} of {isExtensionInstalled
                        ? "2"
                        : "3"}</span
                >
                <div class="step-indicator-content">
                    <div class="step-indicator active" />
                    <div
                        class="step-indicator"
                        class:active={activeStep !== STEPS.STEP1}
                    />
                    {#if !isExtensionInstalled}
                        <div
                            class="step-indicator"
                            class:active={activeStep === STEPS.STEP3}
                        />
                    {/if}
                </div>
            </div>

            {#if activeStep === STEPS.STEP1}
                <div class="header">
                    <h3 class="title">Let's customize your experience</h3>
                    <h2 class="subtitle">What best describes you?</h2>
                </div>
                <div class="options-wrapper">
                    {#each roleOptions as role (role)}
                        <div
                            class="options-item"
                            class:selected={selectedStatuses.includes(
                                role.label,
                            )}
                            use:clickAction={() => toggleRole(role.label)}
                        >
                            <div class="content">
                                <h3>{role.label}</h3>
                                {#if role.valueProposition}
                                    <div class="value-proposition-tag">
                                        <SvgIcon
                                            name="arrow-trending-up"
                                            size={14}
                                            height={14}
                                        />
                                        <p class="small-paragraph">
                                            {@html role.valueProposition}
                                        </p>
                                    </div>
                                {/if}
                            </div>
                            <p class="small-paragraph">{role.description}</p>
                        </div>
                    {/each}
                    <div
                        class="options-item"
                        class:selected={otherRoleActive}
                        use:clickAction={toggleOtherRoleActive}
                    >
                        <div class="content">
                            <h3>Other</h3>
                        </div>
                        <p class="small-paragraph">Tell us your case</p>
                        {#if otherRoleActive}
                            <div class="input-wrapper">
                                <!-- svelte-ignore a11y-autofocus -->
                                <input
                                    type="text"
                                    class="other-input-field"
                                    placeholder="Enter your use-case"
                                    on:click={(e) => e.stopPropagation()}
                                    bind:value={otherRoleValue}
                                    autofocus
                                />
                            </div>
                        {/if}
                    </div>
                </div>
                <div class="footer">
                    <button
                        on:click={stepOneAction}
                        disabled={!selectedRoles.length && !otherRoleValue}
                        class="button"
                    >
                        Continue
                    </button>
                </div>
            {:else if activeStep === STEPS.STEP2}
                <div class="header">
                    <h3 class="title">Let's customize your experience</h3>
                    <h2 class="subtitle">What is the size of your team?</h2>
                </div>

                <div class="options-wrapper">
                    {#each teamSizeOptions as teamSize (teamSize)}
                        <div
                            class="options-item"
                            class:selected={selectedTeamSize === teamSize}
                            use:clickAction={() => updateTeamSize(teamSize)}
                        >
                            <h3>{teamSize}</h3>
                        </div>
                    {/each}
                </div>

                <div class="footer">
                    <button
                        on:click={stepTwoAction}
                        disabled={!selectedTeamSize}
                        class="button step-two"
                    >
                        {isExtensionInstalled
                            ? "Start collabwriting!"
                            : "Continue"}
                    </button>
                    <button
                        on:click={() => updateStep(STEPS.STEP1)}
                        class="button button-outline"
                    >
                        Back
                    </button>
                </div>
            {:else if activeStep === STEPS.STEP3}
                <div class="header">
                    <h2 class="subtitle">Now get started!</h2>
                    <span
                        >Your first step is to install Browser Extension to
                        start collecting snippets and collaborating on online
                        resources</span
                    >
                </div>
                <div class="options-wrapper video-wrapper">
                    <!-- svelte-ignore a11y-media-has-caption -->
                    <video
                        src="./videos/onboarding-video.mp4"
                        autoplay
                        loop
                        aria-hidden="true"
                    />
                </div>
                <a
                    on:click={() => (questionaireVisible = false)}
                    href={isChromeBrowser
                        ? CHROME_STORE_EXTENSION_URL
                        : EDGE_STORE_EXTENSION_URL}
                    class="button link"
                    target="_blank"
                    rel="noreferrer"
                >
                    Install browser extension
                </a>
                <button
                    on:click={() => (questionaireVisible = false)}
                    class="button button-outline"
                >
                    Skip this step
                </button>
            {/if}
        </div>
    </div>
{/if}

<style>
    .questionnaire-wrapper {
        @apply fixed left-0 top-0 z-[2147483647] flex h-dvh w-full flex-col items-center justify-start bg-white;
    }
    .questionnaire-content {
        @apply flex h-screen max-h-screen w-full flex-col overflow-hidden p-4 sm:h-auto sm:w-[560px] sm:pt-24;
    }
    .logo {
        @apply w-44 self-start py-2 sm:fixed sm:left-4 sm:top-4;
    }
    .active-step-number {
        @apply text-xs;
    }
    .step-indicator-wrapper {
        @apply mb-4;
    }
    .step-indicator-content {
        @apply flex gap-0.5;
    }
    .step-indicator {
        @apply h-1 w-6 bg-gray-300;
    }
    .step-indicator.active {
        @apply bg-yellow-300;
    }
    .header {
        @apply relative mb-4 flex flex-col;
    }
    .header .title {
        @apply m-0 text-lg;
    }
    .header .subtitle {
        @apply m-0 text-2xl font-bold;
    }
    .footer {
        @apply relative flex flex-col;
    }
    .options-wrapper {
        @apply mb-4 flex flex-grow flex-col gap-4 overflow-y-auto py-4;
    }
    .options-wrapper.video-wrapper {
        @apply overflow-hidden;
    }
    .options-wrapper video {
        @apply mb-4 h-full max-h-full rounded-xl;
    }
    .header::before {
        content: "";
        @apply absolute bottom-0 z-50 -mb-8 block h-4 w-full bg-gradient-to-b from-white to-transparent !important;
    }
    .footer::after {
        content: "";
        @apply absolute top-0 z-50 -mt-8 block h-4 w-full bg-gradient-to-t from-white to-transparent !important;
    }
    .options-item {
        @apply m-0.5 cursor-pointer rounded-2xl px-4 py-2 outline outline-1 outline-gray-200;
    }
    .options-item.selected {
        @apply outline-2 outline-blue-400;
    }
    .options-item .content {
        @apply flex flex-col items-start justify-between sm:flex-row;
    }
    .options-item h3 {
        @apply m-0 text-lg font-bold;
    }
    .options-item .value-proposition-tag {
        @apply -mr-2 flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-blue-400;
    }
    .options-item .value-proposition-tag .small-paragraph {
        @apply text-xs;
    }
    .options-item .small-paragraph {
        @apply m-0 text-sm;
    }
    .button {
        @apply m-0.5 cursor-pointer rounded-2xl border-none bg-blue-400 p-4 text-xl font-bold text-white;
    }
    .button:disabled {
        @apply bg-gray-200 text-gray-400;
    }
    .button.link {
        @apply text-center;
    }
    .button.button-outline {
        @apply mt-4 bg-transparent text-black outline outline-1 outline-gray-200;
    }
    .input-wrapper {
        @apply mb-2 flex w-full;
    }
    .other-input-field {
        @apply mt-4 flex-grow rounded-full border border-none border-gray-200 bg-gray-100 p-2 pl-4 text-sm focus-visible:outline-none;
    }
</style>
